/* CE NZ: collect source pages before filtering/paginating, so there are no
 * LR3-shaped holes, duplicated featured products, or incorrect product counts.
 * Source-page navigation remains usable if loading fails or JavaScript is off. */
if (!customElements.get('ce-range-grid')) {
  customElements.define('ce-range-grid', class extends HTMLElement {
    async connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.grid = this.querySelector('[data-ce-grid]');
      this.count = this.querySelector('[data-ce-count]');
      this.nav = this.querySelector('[data-ce-pagination]');
      this.tools = this.querySelector('[data-ce-tools]');
      this.searchInput = this.querySelector('[data-ce-search-input]');
      this.sortInput = this.querySelector('[data-ce-sort]');
      this.stockInput = this.querySelector('[data-ce-stock]');
      this.empty = this.querySelector('[data-ce-empty]');
      this.originalCount = this.count.textContent;
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 16);
      const localController = this.controller;
      const sourcePages = Math.max(1, Number(this.dataset.sourcePages) || 1);
      const sourcePage = Math.max(1, Number(this.dataset.sourcePage) || 1);
      let cards = [...this.grid.querySelectorAll('[data-ce-product-id]')];
      try {
        if (sourcePages > 1) {
          this.count.textContent = 'Loading the complete collection...';
          const batches = new Map([[sourcePage, cards]]);
          for (let page = 1; page <= sourcePages; page += 1) {
            if (page === sourcePage) continue;
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(page));
            url.searchParams.set('section_id', this.dataset.sectionId);
            url.searchParams.delete('ce_page');
            const response = await fetch(url.toString(), { signal: localController.signal, credentials: 'same-origin' });
            if (!response.ok) throw new Error(`Collection page ${page}: HTTP ${response.status}`);
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const source = [...doc.querySelectorAll('ce-range-grid')].find(node => node.dataset.range === this.dataset.range);
            if (!source || Number(source.dataset.sourcePage) !== page || Number(source.dataset.sourcePages) !== sourcePages) throw new Error('Unexpected collection response');
            batches.set(page, [...source.querySelectorAll('[data-ce-product-id]')]);
          }
          cards = [...batches.entries()].sort((a,b) => a[0]-b[0]).flatMap(entry => entry[1]);
        }
        // Complete additional model-specific sources without a 50-item cap.
        // The initial Liquid response already includes their first page, so
        // deduplication below also prevents overlap between accessory/parts lists.
        const extraSources = JSON.parse(this.dataset.extraSources || '[]');
        for (const extra of extraSources) {
          this.count.textContent = 'Loading the complete collection...';
          const sourceUrl = new URL(extra.url, window.location.origin);
          if (sourceUrl.origin !== window.location.origin) throw new Error('Invalid collection source');
          const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (previewId) sourceUrl.searchParams.set('preview_theme_id', previewId);
          sourceUrl.searchParams.set('section_id', 'ce-range-source');
          let pages = 1;
          for (let page = 1; page <= pages; page += 1) {
            sourceUrl.searchParams.set('page', String(page));
            const response = await fetch(sourceUrl.toString(), {signal: localController.signal, credentials: 'same-origin'});
            if (!response.ok) throw new Error(`Additional collection: HTTP ${response.status}`);
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const source = doc.querySelector('[data-ce-range-source]');
            if (!source || Number(source.dataset.ceSourcePage) !== page) throw new Error('Unexpected collection source page');
            const reportedPages = Number(source.dataset.ceSourcePages);
            if (!Number.isInteger(reportedPages) || reportedPages < 1 || reportedPages > 500) throw new Error('Invalid source pagination');
            if (page === 1) pages = reportedPages;
            else if (pages !== reportedPages) throw new Error('Collection changed during loading');
            cards.push(...source.querySelectorAll('[data-ce-product-id]'));
          }
        }
        if (!this.isConnected || this.controller !== localController) return;
        const unique = new Map();
        for (const card of cards) if (!unique.has(card.dataset.ceProductId)) unique.set(card.dataset.ceProductId, card);
        this.items = [...unique.values()].map((node, index) => ({node,index}));
        const params = new URL(window.location.href).searchParams;
        this.searchInput.value = params.get('ce_q') || '';
        this.stockInput.checked = params.get('ce_stock') === '1';
        const chosenSort = params.get('ce_sort') || this.dataset.initialSort || 'manual';
        this.sortInput.value = [...this.sortInput.options].some(o => o.value === chosenSort) ? chosenSort : 'manual';
        this.currentPage = Math.max(1, parseInt(params.get('ce_page') || '1',10) || 1);
        const opts = {signal: localController.signal};
        this.searchInput.addEventListener('input', () => { this.currentPage=1; this.render(true); }, opts);
        this.sortInput.addEventListener('change', () => { this.currentPage=1; this.render(true); }, opts);
        this.stockInput.addEventListener('change', () => { this.currentPage=1; this.render(true); }, opts);
        this.tools.hidden = false;
        this.querySelector('[data-ce-source-pagination]').hidden = true;
        this.render(false);
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Do not advertise a total or reorder incomplete data on a failed fetch.
        this.count.textContent = this.originalCount.trim() + '. Use the page links below to browse the remaining products.';
        console.warn('Cat Evolution range: using server-page fallback.', error);
      }
    }
    render(updateUrl) {
      const normalise = (text) => text.trim().toLocaleLowerCase().replace(/[\u00ae\u2122]/g, '');
      const query = normalise(this.searchInput.value);
      const stockOnly = this.stockInput.checked;
      const sort = this.sortInput.value;
      let visible = this.items.filter(({node}) => (!stockOnly || node.dataset.ceAvailable === 'true') && (!query || normalise(node.dataset.ceSearch).includes(query)));
      const titleCompare = (a,b) => a.node.dataset.ceTitle.localeCompare(b.node.dataset.ceTitle,undefined,{numeric:true,sensitivity:'base'});
      visible.sort((a,b) => {
        const ad=a.node.dataset, bd=b.node.dataset;
        switch(sort) {
          case 'title-ascending': return titleCompare(a,b) || a.index-b.index;
          case 'title-descending': return titleCompare(b,a) || a.index-b.index;
          case 'price-ascending': return Number(ad.cePrice)-Number(bd.cePrice) || a.index-b.index;
          case 'price-descending': return Number(bd.cePrice)-Number(ad.cePrice) || a.index-b.index;
          case 'created-descending': return Number(bd.ceCreated)-Number(ad.ceCreated) || a.index-b.index;
          case 'created-ascending': return Number(ad.ceCreated)-Number(bd.ceCreated) || a.index-b.index;
          default: return Number(bd.ceFeatured === 'true')-Number(ad.ceFeatured === 'true') || a.index-b.index;
        }
      });
      const pages = Math.max(1, Math.ceil(visible.length / this.pageSize));
      this.currentPage = Math.min(Math.max(1,this.currentPage),pages);
      const start=(this.currentPage-1)*this.pageSize;
      this.grid.replaceChildren(...visible.slice(start,start+this.pageSize).map(item=>item.node));
      this.empty.hidden = visible.length !== 0;
      this.count.textContent = visible.length === 0 ? '0 products' : `${visible.length} ${visible.length === 1 ? 'product' : 'products'}${pages > 1 ? ` - showing ${start+1}-${Math.min(start+this.pageSize,visible.length)}` : ''}`;
      this.nav.replaceChildren(); this.nav.hidden = pages <= 1;
      if (pages > 1) {
        const previous=document.createElement('button'), next=document.createElement('button'), label=document.createElement('span');
        previous.type=next.type='button'; previous.textContent='Previous'; next.textContent='Next';
        previous.disabled=this.currentPage<=1; next.disabled=this.currentPage>=pages;
        label.textContent=`Page ${this.currentPage} of ${pages}`;
        const change=(delta)=> { this.currentPage+=delta; this.render(true); this.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'}); this.nav.querySelector(delta>0?'button:last-child':'button:first-child')?.focus({preventScroll:true}); };
        previous.addEventListener('click',()=>change(-1)); next.addEventListener('click',()=>change(1));
        this.nav.append(previous,label,next);
      }
      if (updateUrl) {
        const url=new URL(window.location.href);
        url.searchParams.delete('page'); url.searchParams.delete('section_id');
        for (const [key,value] of [['ce_page',this.currentPage>1?String(this.currentPage):''],['ce_sort',sort==='manual'?'':sort],['ce_q',query],['ce_stock',stockOnly?'1':'']]) {
          if (value) url.searchParams.set(key,value); else url.searchParams.delete(key);
        }
        // Native sort parameters are replaced by this complete-list sort, not
        // mixed with the source page order. Storefront filter parameters remain.
        url.searchParams.delete('sort_by');
        history.replaceState(history.state,'',url);
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller=null; }
  });
}
